/*
  © 2025 rhytmelo
  https://www.rhytmelo.com
*/

class ProductForm extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector('form');

    // Debugging: Check if the form is found
    if (!this.form) {
      console.error("❌ ProductForm Error: No form found inside <product-form>. Make sure your Liquid template includes <product-form>.");
      return;
    }

    this.productOptions = this.form.querySelectorAll('.product-options [name*="option"]');
    this.atcBtn = this.form.querySelector('.btn-atc');
    this.buyBtn = this.form.querySelector('.btn-buy');

    this.form.addEventListener('submit', event => this.onSubmit(event));

    this.productOptions.forEach(elem =>
      elem.addEventListener('change', event => this.onChangeProductOption(event))
    );

    this.buyBtn?.addEventListener('click', event => this.onClickBuyBtn(event));

    this.querySelectorAll('select[name="id"]').forEach(select => {
      select.addEventListener('change', () => this.onChangeVariant(select));
    });

    this.handleOutOfStockOptions();
  }

  onChangeProductOption(event) {
    try {
      const selectedOptions = [];

      // Collect all selected options
      this.productOptions.forEach(option => {
        const position = option.dataset.optionPosition;
        if (!position) return;

        if (option.type === 'radio' && option.checked) {
          selectedOptions[position - 1] = option.value;
        } else if (option.tagName === 'SELECT') {
          selectedOptions[position - 1] = option.value;
        }
      });

      // Find matching variant
      const variants = JSON.parse(this.form.dataset.variants || '[]');
      const matchingVariant = variants.find(variant => {
        return variant.options.every((optionValue, index) => {
          return selectedOptions[index] === optionValue;
        });
      });

      if (matchingVariant) {
        this.updateVariant(matchingVariant);
      }
    } catch (error) {
      console.error('Error handling product option change:', error);
    }
  }

  onChangeVariant(select) {
    try {
      const variantId = select.value;
      const variants = JSON.parse(this.form.dataset.variants || '[]');
      const selectedVariant = variants.find(variant => variant.id == variantId);

      if (selectedVariant) {
        this.updateVariant(selectedVariant);
      }
    } catch (error) {
      console.error('Error handling variant change:', error);
    }
  }

  updateVariant(variant) {
    // Update hidden variant ID input
    const variantInput = this.form.querySelector('input[name="id"]');
    if (variantInput) {
      variantInput.value = variant.id;
    }

    // Update price display
    this.updatePriceDisplay(variant);

    // Update stock status
    this.updateStockStatus(variant);

    // Update product options to reflect current variant
    this.updateProductOptions(variant);

    // Dispatch custom events for other components
    window.dispatchEvent(new CustomEvent('kt.product.variant.change', {
      detail: variant
    }));

    // Fetch updated content for components that need it
    this.fetchUpdatedContent(variant);
  }

  updatePriceDisplay(variant) {
    // Find elements within the current product form context
    const productContainer = this.form.closest('.product-contents') || this.form.closest('.product-main') || document;
    const priceElement = productContainer.querySelector('.product-price-final');
    const comparePriceElement = productContainer.querySelector('.product-price-compare');
    const saleBadge = productContainer.querySelector('.product-sale-badge');
    const soldOutBadge = productContainer.querySelector('.product-sold-out-badge');

    // Defensive check: ensure we have required pricing data
    const hasDiscount = variant.compare_at_price && variant.compare_at_price > variant.price;

    if (priceElement) {
      const formattedPrice = this.formatMoney(variant.price);
      priceElement.innerHTML = formattedPrice;

      // Update price classes
      if (hasDiscount) {
        priceElement.classList.add('product-price-final-sale');
      } else {
        priceElement.classList.remove('product-price-final-sale');
      }
    }

    if (comparePriceElement) {
      if (hasDiscount) {
        const formattedComparePrice = this.formatMoney(variant.compare_at_price);
        comparePriceElement.innerHTML = formattedComparePrice;
        comparePriceElement.closest('s').removeAttribute('hidden');
      } else {
        comparePriceElement.closest('s').setAttribute('hidden', '');
      }
    }

    if (saleBadge) {
      if (hasDiscount) {
        const discountType = saleBadge.dataset.discountType;
        let discountText = '';

        if (discountType === 'percentage') {
          const percentage = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
          discountText = `${percentage}%`;
        } else {
          const formattedSavings = this.formatMoney(variant.compare_at_price - variant.price);
          discountText = formattedSavings;
        }

        saleBadge.innerHTML = `Save ${discountText}`;
        saleBadge.removeAttribute('hidden');
      } else {
        saleBadge.setAttribute('hidden', '');
      }
    }

    if (soldOutBadge) {
      if (variant.available) {
        soldOutBadge.setAttribute('hidden', '');
      } else {
        soldOutBadge.removeAttribute('hidden');
      }
    }
  }

  updateStockStatus(variant) {
    const atcBtn = this.form.querySelector('.btn-atc');

    if (atcBtn) {
      if (variant.available) {
        atcBtn.disabled = false;
        atcBtn.textContent = atcBtn.dataset.addToCartText || 'Add to cart';
      } else {
        atcBtn.disabled = true;
        atcBtn.textContent = atcBtn.dataset.soldOutText || 'Sold out';
      }
    }
  }

  updateProductOptions(variant) {
    // Update option selections to match the variant
    variant.options.forEach((optionValue, index) => {
      const position = index + 1;

      // Update radio buttons
      const radioOption = this.form.querySelector(`input[type="radio"][data-option-position="${position}"][value="${optionValue}"]`);
      if (radioOption && !radioOption.checked) {
        radioOption.checked = true;
      }

      // Update select dropdowns
      const selectOption = this.form.querySelector(`select[data-option-position="${position}"]`);
      if (selectOption && selectOption.value !== optionValue) {
        selectOption.value = optionValue;
      }
    });
  }

  async fetchUpdatedContent(variant) {
    try {
      console.log(`ProductForm: Fetching variant ${variant.id}...`)
      const response = await fetch(`${this.form.dataset.productUrl}?variant=${variant.id}`);
      if (response.ok) {
        const html = await response.text();
        const newDoc = new DOMParser().parseFromString(html, 'text/html');

        // Check if cross-sells exists in response
        const crossSellsInResponse = newDoc.querySelector('cross-sells')
        console.log(`ProductForm: Variant response includes cross-sells: ${!!crossSellsInResponse}`)

        // Dispatch event with new document for components that need to update
        window.dispatchEvent(new CustomEvent('kt.product.variant.change.newDoc', {
          detail: newDoc
        }));
      }
    } catch (error) {
      console.warn('ProductForm: Failed to fetch updated variant content:', error);
    }
  }

  handleOutOfStockOptions() {
    try {
      const variants = JSON.parse(this.form.dataset.variants || '[]');

      // Mark unavailable options
      this.productOptions.forEach(option => {
        const position = option.dataset.optionPosition;
        const value = option.value;

        if (!position || !value) return;

        // Check if this option value has any available variants
        const hasAvailableVariant = variants.some(variant => {
          return variant.available && variant.options[position - 1] === value;
        });

        if (!hasAvailableVariant) {
          option.disabled = true;
          if (option.type === 'radio') {
            const label = option.closest('li') || option.nextElementSibling;
            if (label) {
              label.classList.add('disabled', 'text-muted');
            }
          }
        }
      });
    } catch (error) {
      console.warn('Error handling out of stock options:', error);
    }
  }

  onSubmit(event) {
    event.preventDefault();

    const atcBtn = this.form.querySelector('.btn-atc');
    if (atcBtn && !atcBtn.disabled) {
      this.addToCart();
    }
  }

  onClickBuyBtn(event) {
    event.preventDefault();

    const formData = new FormData(this.form);
    const params = new URLSearchParams(formData).toString();

    // Redirect to checkout with the current variant
    window.location.href = `/cart/add?${params}&return_to=/checkout`;
  }

  async addToCart() {
    const atcBtn = this.form.querySelector('.btn-atc');

    if (atcBtn) {
      // Set loading state
      atcBtn.style.width = `${atcBtn.offsetWidth}px`;
      atcBtn.classList.add('loading');
      atcBtn.disabled = true;
      atcBtn.setAttribute('aria-busy', 'true');

      try {
        const formData = new FormData(this.form);
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        });

        if (response.ok) {
          const result = await response.json();

          // Dispatch success event
          window.dispatchEvent(new CustomEvent('kt.cart.added', {
            detail: result
          }));

          // Update cart count if element exists
          const cartCount = document.querySelector('[data-cart-count]');
          if (cartCount) {
            const cartResponse = await fetch('/cart.js');
            const cart = await cartResponse.json();
            cartCount.textContent = cart.item_count;
          }
        } else {
          const error = await response.json();
          console.error('Add to cart error:', error);
          alert(error.message || 'Unable to add item to cart');
        }
      } catch (error) {
        console.error('Add to cart error:', error);
        alert('Unable to add item to cart');
      } finally {
        // Remove loading state
        atcBtn.style.width = '';
        atcBtn.classList.remove('loading');
        atcBtn.disabled = false;
        atcBtn.removeAttribute('aria-busy');
      }
    }
  }

  formatMoney(cents) {
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents).replace('.00', '');
    }

    // Fallback formatting if Shopify.formatMoney is not available
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`.replace('.00', '');
  }
}

class ProductMediaGallery extends HTMLElement {
  constructor () {
    super()

    try {
      const initialSlide = Number(this.dataset.initialSlide)
      
      const thumbsEl = this.querySelector('.swiper-thumbs')
      const mainEl = this.querySelector('.swiper-main')
      
      if (!thumbsEl || !mainEl) {
        console.warn('ProductMediaGallery: Swiper elements not found')
        return
      }

      /** @type {Swiper} */
      this.thumbs = new window.Swiper(thumbsEl, this.thumbsOptions())

      /** @type {Swiper} */
      this.main = new window.Swiper(mainEl, this.mainOptions())

      this.main.slideTo(initialSlide)

      this.setStickyPosition()
      this.listenVariantChange()
      this.initFullScreenGallery()
    } catch (error) {
      console.error('ProductMediaGallery: Initialization error:', error)
    }
  }

  thumbsOptions () {
    /** @type {SwiperOptions} */
    const options = {
      slidesPerView: Number(this.dataset.thumbsPerView),
      spaceBetween: 10,
      mousewheel: true
    }
    return options
  }

  mainOptions () {
    /** @type {SwiperOptions} */
    const options = {
      effect: this.dataset.effect,
      speed: Number(this.dataset.speed),
      rewind: true,
      pagination: {
        type: this.dataset.paginationType,
        el: '.swiper-pagination',
        dynamicBullets: true,
        dynamicMainBullets: 2,
        renderFraction: function (currentClass, totalClass) {
          return `<span class="${currentClass}"></span>/<span class="${totalClass}"></span>`
        }
      },
      navigation: {
        nextEl: this.querySelector('.swiper-button-next'),
        prevEl: this.querySelector('.swiper-button-prev')
      },
      thumbs: {
        swiper: this.thumbs,
        autoScrollOffset: 1
      },
      cubeEffect: {
        shadow: false
      },
      creativeEffect: {
        prev: {
          shadow: true,
          origin: 'left center',
          translate: ['-5%', 0, -200],
          rotate: [0, 100, 0]
        },
        next: {
          origin: 'right center',
          translate: ['5%', 0, -200],
          rotate: [0, -100, 0]
        }
      },
      scrollbar: {
        enabled: this.dataset.scrollbar === 'true',
        el: this.querySelector('.swiper-scrollbar'),
        draggable: true,
        hide: true
      },
      breakpoints: {
        0: { slidesPerView: 1 * (this.dataset.partialSlidesMobile === 'true' ? 1.2 : 1) },
        600: { slidesPerView: 1 }
      },
      on: {
        slideChange: this.onSlideChange.bind(this)
      }
    }
    return options
  }

  onSlideChange () {
    this.querySelectorAll('video').forEach(video => video.pause())

    setTimeout(() => {
      if (this.querySelector('.swiper-main .swiper-slide-active video')) {
        this.querySelector('.swiper-main-wrapper').setAttribute('data-active-slide-is-video', 'true')
      } else {
        this.querySelector('.swiper-main-wrapper').setAttribute('data-active-slide-is-video', 'false')
      }
    }, 500)
  }

  setStickyPosition () {
    if (this.closest('.product-main') && window.matchMedia('(min-width: 1200px)').matches) {
      const headerGroup = document.querySelector('#header-group.sticky-top')
      const pdpExtraContent = document.querySelector('#pdp-extra-content')

      if (!headerGroup) {
        console.warn('ProductMediaGallery: header-group not found, skipping sticky positioning')
        return
      }

      let oldScroll = window.scrollY

      window.addEventListener('scroll', () => {
        try {
          const newScroll = window.scrollY
          if (newScroll > oldScroll) {
            if (newScroll > headerGroup.clientHeight) {
              this.style.top = '20px'
              if (pdpExtraContent) {
                pdpExtraContent.style.top = '20px'
              }
            }
          } else if (newScroll < oldScroll) {
            this.style.top = `${headerGroup.clientHeight + 20}px`
            if (pdpExtraContent) {
              pdpExtraContent.style.top = `${headerGroup.clientHeight + 20}px`
            }
          }

          oldScroll = Math.max(window.scrollY, 0)
        } catch (error) {
          console.warn('ProductMediaGallery: Error in scroll listener:', error)
        }
      })
    }
  }

  listenVariantChange () {
    window.addEventListener('kt.product.variant.change', (event) => {
      const selectedVariant = event.detail

      if (selectedVariant.featured_media) {
        this.main.slideTo(selectedVariant.featured_media.position - 1)
      }
    }, false)
  }

  async initFullScreenGallery () {
    if (this.dataset.fullScreen === 'false') return

    const vendorScript = document.querySelector('script[src*="vendor-glightbox.min"]')

    vendorScript.onload = () => {
      window.GLightbox({
        selector: `[data-gallery="gallery-${this.dataset.productId}"]`,
        loop: true,
        openEffect: 'zoom',
        closeEffect: 'fade',
        slideEffect: this.dataset.fullScreenEffect
      })
    }
  }
}
customElements.define('product-media-gallery', ProductMediaGallery)

class QtySwitcher extends HTMLElement {
  constructor () {
    super()

    this.qtyInput = this.querySelector('input')
    this.minusBtn = this.querySelector('button.btn-qty-minus')
    this.plusBtn = this.querySelector('button.btn-qty-plus')

    this.qtyInput.addEventListener('change', () => this.onQtyInputChange())
    this.minusBtn.addEventListener('click', () => this.onMinusBtnClick())
    this.plusBtn.addEventListener('click', () => this.onPlusBtnClick())

    window.addEventListener('kt.product.variant.change.newDoc', (event) => this.onVariantChange(event))
  }

  onQtyInputChange () {
    this.validateButtons()
  }

  onMinusBtnClick () {
    this.qtyInput.value = Number(this.qtyInput.value) - 1
    this.validateButtons()
    this.qtyInput.dispatchEvent(new CustomEvent('change'))
  }

  onPlusBtnClick () {
    this.qtyInput.value = Number(this.qtyInput.value) + 1
    this.validateButtons()
    this.qtyInput.dispatchEvent(new CustomEvent('change'))
  }

  validateButtons () {
    if (this.qtyInput.getAttribute('min') && Number(this.qtyInput.value) <= Number(this.qtyInput.getAttribute('min'))) {
      this.minusBtn.disabled = true
      this.qtyInput.value = this.qtyInput.getAttribute('min')
    } else {
      this.minusBtn.disabled = false
    }
    if (this.qtyInput.getAttribute('max') && Number(this.qtyInput.value) >= Number(this.qtyInput.getAttribute('max'))) {
      this.plusBtn.disabled = true
      this.qtyInput.value = this.qtyInput.getAttribute('max')
    } else {
      this.plusBtn.disabled = false
    }
  }

  onVariantChange (event) {
    if (!this.closest('.product-block-atc')) return

    const newDoc = event.detail
    const newQtyInput = newDoc.querySelector('.product-block-atc qty-switcher input')

    this.qtyInput.setAttribute('max', newQtyInput.getAttribute('max'))
    this.validateButtons()
  }
}
customElements.define('qty-switcher', QtySwitcher)

class ProductStockIndicator extends HTMLElement {
  constructor () {
    super()

    this.progressBar = this.querySelector('.progress-bar')
    this.animateProgressBar()
    this.handleVariantChange()
  }

  animateProgressBar () {
    setTimeout(() => {
      this.progressBar.style.width = this.progressBar.dataset.width
    }, 250)
  }

  handleVariantChange () {
    window.addEventListener('kt.product.variant.change', async () => {
      console.log('ProductStockIndicator: kt.product.variant.change event fired')
      this.querySelector('.product-block-stock-indicator-inner').style.opacity = '.25'
    })

    window.addEventListener('kt.product.variant.change.newDoc', async (e) => {
      console.log('ProductStockIndicator: kt.product.variant.change.newDoc event fired')
      const newDoc = e.detail

      this.replaceWith(newDoc.querySelector('[id*="product-block-stock-indicator"]'))
      this.animateProgressBar()
    })
  }
}
customElements.define('product-stock-indicator', ProductStockIndicator)

class ProductQuantityBreaks extends HTMLElement {
  constructor () {
    super()

    this.handleInputChange()
    this.handleVariantChange()
  }

  handleInputChange (btn) {
    this.querySelectorAll('input').forEach(input => {
      input.addEventListener('change', () => {
        const qtyField = input.closest('form').querySelector('[name="quantity"]')
        qtyField.value = input.value
        qtyField.dispatchEvent(new CustomEvent('change'))

        const atcBtn = this.closest('form').querySelector('.btn-atc')

        setTimeout(() => {
          atcBtn.classList.add('animate__animated', 'animate__shakeX')
        }, 250)

        setTimeout(() => {
          atcBtn.classList.remove('animate__animated', 'animate__shakeX')
        }, 1500)
      })
    })
  }

  handleVariantChange () {
    window.addEventListener('kt.product.variant.change', async () => {
      console.log('ProductQuantityBreaks: kt.product.variant.change event fired')
      this.querySelector('.product-block-quantity-breaks-list').style.opacity = '.25'
    })

    window.addEventListener('kt.product.variant.change.newDoc', async (e) => {
      console.log('ProductQuantityBreaks: kt.product.variant.change.newDoc event fired')
      const newDoc = e.detail

      this.replaceWith(newDoc.querySelector('product-quantity-breaks'))
      this.handleInputChange()
    })
  }
}
customElements.define('product-quantity-breaks', ProductQuantityBreaks)

class ProductQuickButton extends HTMLElement {
  constructor () {
    super()

    this.btn = this.querySelector('.btn-quick-view')
    this.modalCustomElem = document.querySelector('product-quick-view-modal')

    this.btn.addEventListener('click', async (event) => this.fetchProductData(event))
  }

  async fetchProductData (event) {
    event.preventDefault()

    this.setLoading(true)

    const response = await fetch(this.btn.dataset.productUrl)

    if (response.ok) {
      const text = await response.text()
      const newDoc = new DOMParser().parseFromString(text, 'text/html')

      document.querySelector('product-quick-view-modal').showModal(newDoc)
    } else {
      // TODO: Handle Errors
    }

    this.setLoading(false)
  }

  setLoading (isLoading) {
    if (isLoading) {
      this.btn.classList.add('loading')
      this.btn.disabled = true
      this.btn.setAttribute('aria-busy', 'true')
    } else {
      this.btn.classList.remove('loading')
      this.btn.disabled = false
      this.btn.removeAttribute('aria-busy')
    }
  }
}
customElements.define('product-quick-view-button', ProductQuickButton)

class ProductQuickViewModal extends HTMLElement {
  constructor () {
    super()

    this.modalElem = document.querySelector('#quick-view-modal')

    this.modalElem.addEventListener('hidden.bs.modal', () => {
      this.modalElem.querySelector('.product').innerHTML = '<div data-quick-view-contents></div>'
    })

    window.addEventListener('kt.cart.added', () => {
      window.bootstrap.Modal.getOrCreateInstance(this.modalElem).hide()
    })
  }

  async showModal (newDoc) {
    this.modalElem.querySelector('[data-quick-view-contents]').replaceWith(
      newDoc.querySelector('.product-main [data-quick-view-contents]'))

    this.modalElem.querySelector('.product-title').setAttribute('id', 'quick-view-modal-label')

    this.modalElem.querySelectorAll('.collapse-wrapper').forEach(collapseWrapper => {
      collapseWrapper.querySelector('[data-bs-toggle="collapse"]').setAttribute('aria-expanded', 'false')
      collapseWrapper.querySelector('.collapse').classList.remove('show')
    })

    this.modalElem.querySelectorAll('.product-options [name*="option"]').forEach(elem => {
      elem.setAttribute('name', `${elem.name}-qv`)
      elem.setAttribute('id', `${elem.id}-qv`)
    })

    this.modalElem.querySelectorAll('.product-options label').forEach(label => {
      label.setAttribute('for', `${label.getAttribute('for')}-qv`)
    })

    this.modalElem.querySelectorAll('.collapse').forEach(elem => {
      elem.addEventListener('shown.bs.collapse', () => {
        elem.closest('.product-block-collapse')?.scrollIntoView({ behavior: 'smooth' })
      })
    })

    window.bootstrap.Modal.getOrCreateInstance(this.modalElem).show()
    window.dispatchEvent(new CustomEvent('kt.product.quick_view.modal_shown'))
  }
}
customElements.define('product-quick-view-modal', ProductQuickViewModal)

class ProductDropupSelector extends HTMLElement {
  constructor () {
    super()

    this.productCard = this.closest('.product-card')
    this.dropdownBtn = this.querySelector('.dropdown-toggle')
    
    if (!this.productCard || !this.dropdownBtn) {
      console.warn('ProductDropupSelector: productCard or dropdownBtn not found')
      return
    }

    const dropdownMenu = this.querySelector('.dropdown-menu')
    if (dropdownMenu) {
      dropdownMenu.style.maxHeight = 
        this.productCard.offsetHeight - this.dropdownBtn.offsetHeight - 2 + 'px'
    }

    this.addEventListener('show.bs.dropdown', (event) => {
      try {
        const isMobile = window.matchMedia('(max-width: 499px').matches

        if (isMobile) {
          event.preventDefault()
          this.initOffcanvas()
        } else {
          const imgWrapper = this.productCard?.querySelector('.product-card-img-wrapper')
          if (imgWrapper) {
            imgWrapper.style.opacity = '.2'
          }
        }
      } catch (error) {
        console.warn('ProductDropupSelector: Error in show.bs.dropdown:', error)
      }
    })
    this.addEventListener('hide.bs.dropdown', (event) => {
      try {
        const isMobile = window.matchMedia('(max-width: 499px').matches

        if (isMobile) {
          event.preventDefault()
          this.removeOffcanvas()
        } else {
          const imgWrapper = this.productCard?.querySelector('.product-card-img-wrapper')
          if (imgWrapper) {
            imgWrapper.style.opacity = '1'
          }
        }
      } catch (error) {
        console.warn('ProductDropupSelector: Error in hide.bs.dropdown:', error)
      }
    })

    this.querySelectorAll('.dropdown-menu button').forEach(btn => {
      btn.addEventListener('click', () => this.addToCart(btn))
    })
  }

  async addToCart (btn) {
    window.bootstrap.Dropdown.getOrCreateInstance(this).hide()

    this.dropdownBtn.classList.add('loading')
    this.dropdownBtn.disabled = true
    this.dropdownBtn.setAttribute('aria-busy', 'true')

    const items = [{
      id: Number(btn.dataset.variantId),
      quantity: 1
    }]

    await document.querySelector('cart-container').add({ items })

    this.dropdownBtn.classList.remove('loading')
    this.dropdownBtn.disabled = false
    this.dropdownBtn.setAttribute('aria-busy', 'false')
  }

  initOffcanvas () {
    const html = `
      <div id="product-dropup-select-offcanvas" class="offcanvas offcanvas-bottom" tabindex="-1" aria-labelledby="product-dropup-select-offcanvas-title">
        <div class="offcanvas-header">
          <h5 class="offcanvas-title" id="product-dropup-select-offcanvas-title">
            ${this.dropdownBtn.textContent}
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="offcanvas" aria-label="Close">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" class="svg-icon-x" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <div class="offcanvas-body p-0">
          <ul class="dropdown-menu">
            ${this.querySelector('.dropdown-menu').innerHTML}
          </ul>
        </div>
      </div>
    `
    document.body.insertAdjacentHTML('beforeend', html)

    window.bootstrap.Offcanvas.getOrCreateInstance('#product-dropup-select-offcanvas').show()

    document.querySelectorAll('#product-dropup-select-offcanvas .dropdown-menu button').forEach(btn => {
      btn.addEventListener('click', () => {
        this.addToCart(btn)
        window.bootstrap.Offcanvas.getOrCreateInstance('#product-dropup-select-offcanvas').hide()
      })
    })

    document.querySelector('#product-dropup-select-offcanvas').addEventListener('hidden.bs.offcanvas', () => {
      document.querySelector('#product-dropup-select-offcanvas').remove()
    })
  }
}
customElements.define('product-dropup-selector', ProductDropupSelector)

class CrossSells extends HTMLElement {
  constructor () {
    super()

    try {
      const buttons = this.querySelectorAll('.cross-sell-cta-btn')
      const selects = this.querySelectorAll('.cross-sell-variant-select')
      const atcBtn = this.querySelector('.btn-atc')

      if (!buttons.length) {
        return
      }

      // Ensure all checkboxes are initially checked (selected by default)
      this.querySelectorAll('.cross-sell-checkbox:not(:disabled)').forEach(checkbox => {
        checkbox.checked = true
        this.updateButtonState(checkbox)
      })

      // Handle button clicks to toggle checkbox
      buttons.forEach(button => {
        button.addEventListener('click', (e) => {
          const checkbox = button.querySelector('.cross-sell-checkbox')
          if (checkbox && !button.disabled) {
            checkbox.checked = !checkbox.checked
            this.updateButtonState(checkbox)
            this.updateTotalPrice()
          }
        })
      })

      // Handle variant selection
      selects.forEach(select => {
        select.addEventListener('change', () => this.onChangeVariant(select))
      })

      // Handle ATC button
      if (atcBtn) {
        atcBtn.addEventListener('click', () => this.addToCart(atcBtn))
      }

      // Initialize total price on load
      this.updateTotalPrice()
    } catch (error) {
      // Initialization error - component may not be fully initialized
    }
  }

  updateButtonState (checkbox) {
    const button = checkbox.closest('.cross-sell-cta-btn')
    if (!button) return

    const isSelected = checkbox.checked
    
    if (isSelected) {
      button.classList.add('is-selected')
      button.setAttribute('data-selected', 'true')
      const btnText = button.querySelector('.btn-text')
      if (btnText) {
        btnText.textContent = 'Selected'
      }
    } else {
      button.classList.remove('is-selected')
      button.setAttribute('data-selected', 'false')
      const btnText = button.querySelector('.btn-text')
      if (btnText) {
        btnText.textContent = 'Select this'
      }
    }
  }

  onChangeVariant (select) {
    const variantImage = select[select.selectedIndex].dataset.variantImage
    const variantPrice = select[select.selectedIndex].dataset.price

    if (variantImage?.length) {
      const card = select.closest('.cross-sell-card')
      const img = card?.querySelector('.cross-sell-image-wrapper img')
      if (img) {
        img.setAttribute('src', variantImage)
      }
    }

    // Update price display
    if (variantPrice) {
      const card = select.closest('.cross-sell-card')
      const priceNew = card?.querySelector('.cross-sell-price-new')
      if (priceNew) {
        priceNew.setAttribute('data-variant-price', variantPrice)
        priceNew.textContent = window.Shopify.formatMoney(Number(variantPrice))
      }
    }

    this.updateTotalPrice()
  }

  updateTotalPrice () {
    try {
      let totalPrice = 0
      let totalComparePrice = 0
      let selectedProductCount = 0

      const checkboxes = this.querySelectorAll('.cross-sell-checkbox')

      // Count selected products and calculate totals
      checkboxes.forEach(checkbox => {
        if (checkbox.checked) {
          selectedProductCount++
          const card = checkbox.closest('.cross-sell-card')
          if (!card) return

          let cardPrice = 0
          let cardComparePrice = 0

          // First, try to get price from hidden input
          const inputHidden = card.querySelector('input[name="variant-id"][type="hidden"]')
          if (inputHidden?.dataset.price) {
            cardPrice = Number(inputHidden.dataset.price)
            cardComparePrice = Number(inputHidden.dataset.compareAtPrice || 0)
          }

          // If no hidden input, try to get price from select's selected option
          if (cardPrice === 0) {
            const select = card.querySelector('select[name="variant-id"]')
            if (select && select.selectedIndex >= 0) {
              const option = select[select.selectedIndex]
              cardPrice = Number(option.dataset.price || 0)
              cardComparePrice = Number(option.dataset.compareAtPrice || 0)
            }
          }

          totalPrice += cardPrice
          totalComparePrice += cardComparePrice
        }
      })

      // Determine if discount should apply (2+ products selected)
      const shouldApplyDiscount = selectedProductCount > 1
      let discountAmount = 0
      let finalPrice = totalPrice

      if (shouldApplyDiscount) {
        // Apply 5% discount only when 2+ products are selected
        const discountRate = 0.05
        discountAmount = Math.round(totalPrice * discountRate)
        finalPrice = totalPrice - discountAmount
      }

      // Update total price display
      this.querySelectorAll('[data-total-price-display]').forEach(elem => {
        elem.textContent = window.Shopify.formatMoney(finalPrice)
      })

      // Update original price display
      this.querySelectorAll('[data-total-original-price]').forEach(elem => {
        elem.textContent = window.Shopify.formatMoney(totalComparePrice)
      })

      // Update discount amount display (only shown when discount applies)
      this.querySelectorAll('[data-total-discount-amount]').forEach(elem => {
        elem.textContent = window.Shopify.formatMoney(discountAmount)
      })

      // Calculate and show potential savings in incentive state
      if (!shouldApplyDiscount && totalPrice > 0) {
        const potentialSavings = Math.round(totalPrice * 0.05)
        this.querySelectorAll('[data-potential-savings]').forEach(elem => {
          elem.textContent = window.Shopify.formatMoney(potentialSavings)
        })
      }

      // Show/hide discount savings section (only when discount applies)
      const discountContainer = this.querySelector('[data-discount-savings-container]')
      if (discountContainer) {
        if (shouldApplyDiscount && discountAmount > 0) {
          discountContainer.style.display = 'block'
        } else {
          discountContainer.style.display = 'none'
        }
      }

      // Show/hide incentive message (only when NOT discount state)
      const incentiveContainer = this.querySelector('[data-incentive-message-container]')
      if (incentiveContainer) {
        if (!shouldApplyDiscount && totalPrice > 0) {
          incentiveContainer.style.display = 'block'
        } else {
          incentiveContainer.style.display = 'none'
        }
      }

      // Update incentive notice banner text based on discount state
      const incentiveNotice = this.querySelector('[data-incentive-notice]')
      if (incentiveNotice) {
        const incentiveText = incentiveNotice.querySelector('[data-incentive-text]')
        if (incentiveText) {
          if (shouldApplyDiscount) {
            incentiveText.textContent = 'Get 5% off on all selected items!'
          } else {
            incentiveText.textContent = 'Add more items to unlock 5% savings'
          }
        }
      }

      // Update incentive notice background color based on state
      if (incentiveNotice) {
        if (shouldApplyDiscount) {
          incentiveNotice.style.background = '#f0f9ff'
        } else {
          incentiveNotice.style.background = '#cfe2ff'
        }
      }

      // Update button state
      const atcBtn = this.querySelector('.btn-atc')
      if (atcBtn) {
        atcBtn.disabled = totalPrice === 0
      }

      // Toggle compare price visibility
      if (totalComparePrice > totalPrice) {
        this.querySelectorAll('[data-original-strikethrough]').forEach(elem => {
          elem.removeAttribute('hidden')
        })
      } else {
        this.querySelectorAll('[data-original-strikethrough]').forEach(elem => {
          elem.setAttribute('hidden', 'hidden')
        })
      }
    } catch (error) {
      console.warn('CrossSells: Error updating total price:', error)
    }
  }

  async addToCart (atcBtn) {
    atcBtn.style.width = `${atcBtn.clientWidth}px`
    atcBtn.classList.add('loading')
    atcBtn.disabled = true
    atcBtn.setAttribute('aria-busy', 'true')

    try {
      const items = []
      let selectedCount = 0

      // Collect selected items and count them
      this.querySelectorAll('.cross-sell-checkbox').forEach(checkbox => {
        if (checkbox.checked) {
          selectedCount++
          const card = checkbox.closest('.cross-sell-card')
          const variantInput = card?.querySelector('[name="variant-id"]')
          if (variantInput) {
            const id = Number(variantInput.value)
            items.push({
              id,
              quantity: 1
            })
          }
        }
      })

      // Add items to cart
      const cartContainer = document.querySelector('cart-container')
      await cartContainer.add({ items })

      // If 2+ products selected, apply discount code via AJAX
      if (selectedCount > 1) {
        await this.applyDiscountCode('CROSSSELL5')
      }

      atcBtn.style.width = ''
      atcBtn.classList.remove('loading')
      atcBtn.disabled = false
      atcBtn.setAttribute('aria-busy', 'false')
    } catch (error) {
      console.error('CrossSells: Error adding to cart:', error)
      atcBtn.style.width = ''
      atcBtn.classList.remove('loading')
      atcBtn.disabled = false
      atcBtn.setAttribute('aria-busy', 'false')
    }
  }

  async applyDiscountCode (code) {
    try {
      // Apply discount code via Shopify Cart Update API
      const response = await fetch(`${window.Shopify.routes.cart_update_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          discount: code,
          sections: ['cart-count-badge', 'offcanvas-cart'],
          sections_url: window.location.pathname
        })
      })
      
      if (response.ok) {
        const data = await response.json()
        console.log(`Discount code '${code}' applied successfully`)
        
        // Reload cart sections to reflect discount
        const cartContainer = document.querySelector('cart-container')
        if (cartContainer && data.sections) {
          cartContainer.reloadCartElements(data.sections)
        }
        
        // Dispatch custom event to notify discount has been applied
        window.dispatchEvent(new CustomEvent('kt.discount.applied', { 
          detail: { code, cart: data } 
        }))
      } else {
        console.warn(`Failed to apply discount code '${code}'`)
      }
    } catch (error) {
      console.error('CrossSells: Error applying discount code:', error)
    }
  }
}
customElements.define('cross-sells', CrossSells)

class ProductCard extends HTMLElement {
  constructor () {
    super()

    this.handleCarouselHover()
  }

  handleCarouselHover () {
    if (window.matchMedia('(pointer: coarse)').matches) return

    const bsCarousel = window.bootstrap.Carousel.getOrCreateInstance(this.querySelector('.product-card-img-wrapper .carousel'))
    const carouselInner = this.querySelector('.product-card-img-wrapper .carousel-inner')
    let myInterval

    carouselInner.addEventListener('mouseenter', () => {
      setTimeout(() => {
        bsCarousel.next()
      }, 500)

      myInterval = setInterval(() => {
        bsCarousel.next()
      }, 2000)
    })

    carouselInner.addEventListener('mouseleave', () => {
      clearInterval(myInterval)
    })
  }
}
customElements.define('product-card', ProductCard)

class FileUploadPersonalizationField extends HTMLElement {
  constructor () {
    super()

    this.loadStyleScripts()

    const vendorScript = document.querySelector('script[src*="plugin-image-preview"]')
    vendorScript.onload = () => {
      this.init()
    }

    window.addEventListener('kt.product.quick_view.modal_shown', () => this.init())
  }

  loadStyleScripts () {
    if (document.querySelector('script[src*="plugin-image-preview"]')) return

    const style1 = document.createElement('link')
    style1.setAttribute('href', 'https://cdn.jsdelivr.net/npm/filepond@4.31.1/dist/filepond.min.css')
    style1.setAttribute('rel', 'stylesheet')
    style1.setAttribute('integrity', 'sha256-a95jYCBL4++k1XyLYgulKmY33bIJIVYMsJO/RNytaJM=')
    style1.setAttribute('crossorigin', 'anonymous')
    document.head.appendChild(style1)

    const style2 = document.createElement('link')
    style2.setAttribute('href', 'https://cdn.jsdelivr.net/npm/filepond-plugin-image-preview@4.6.12/dist/filepond-plugin-image-preview.min.css')
    style2.setAttribute('rel', 'stylesheet')
    style2.setAttribute('integrity', 'sha256-YsO8aMI20vSizdmc2mmWx2DU1wof4v60Nwy7hIBvdM8=')
    style2.setAttribute('crossorigin', 'anonymous')
    document.head.appendChild(style2)

    const script1 = document.createElement('script')
    script1.setAttribute('src', 'https://cdn.jsdelivr.net/npm/filepond@4.31.1/dist/filepond.min.js')
    script1.setAttribute('integrity', 'sha256-6yXpr8+sATA4Q2ANTyZmpn4ZGP7grbIRNpe9s0Y+iO0=')
    script1.setAttribute('crossorigin', 'anonymous')
    script1.setAttribute('defer', 'defer')
    document.head.appendChild(script1)

    const script2 = document.createElement('script')
    script2.setAttribute('src', 'https://cdn.jsdelivr.net/npm/filepond-plugin-image-preview@4.6.12/dist/filepond-plugin-image-preview.min.js')
    script2.setAttribute('integrity', 'sha256-1vQHytMrpOsKFyUUPuEVhxeNH6UhMX3uNetDRvlSpwU=')
    script2.setAttribute('crossorigin', 'anonymous')
    script1.setAttribute('defer', 'defer')
    document.head.appendChild(script2)
  }

  async init () {
    await new Promise(resolve => setTimeout(resolve, 200))

    window.FilePond.registerPlugin(window.FilePondPluginImagePreview)
    const inputElem = this.querySelector('input[type="file"]')

    window.FilePond.create(inputElem, {
      storeAsFile: true,
      labelIdle: this.querySelector('[data-label-text]').innerHTML
    })
  }
}
customElements.define('file-upload-personalization-field', FileUploadPersonalizationField)

// Product block collapses
if (window.matchMedia('(max-width: 1199px').matches) {
  document.querySelectorAll('.product-block-collapse').forEach(elem => {
    elem.querySelector('.collapse').addEventListener('shown.bs.collapse', () => {
      elem.scrollIntoView({ behavior: 'smooth' })
    })
  })
}

// Shopify Subscriptions app
const adjustShopifySubscriptionsStyling = () => {
  document.querySelectorAll('.shopify_subscriptions_app_block [type="radio"]').forEach(radio => {
    radio.classList.add('form-check-input')
  })
}
adjustShopifySubscriptionsStyling()
window.addEventListener('kt.product.quick_view.modal_shown', adjustShopifySubscriptionsStyling)

customElements.define('product-form', ProductForm);

// Global error handler to catch null reference errors
window.addEventListener('error', (event) => {
  if (event.message && event.message.includes('style')) {
    console.error('GLOBAL ERROR: Style access error:', {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      stack: event.error?.stack
    })
  }
})

document.addEventListener('DOMContentLoaded', function() {
  if (!document.getElementById('theme-overlay')) {
    const overlay = document.createElement('div');
    overlay.id = 'theme-overlay';
    overlay.style.position = 'fixed';
    overlay.style.top = '0';
    overlay.style.left = '0';
    overlay.style.width = '100%';
    overlay.style.height = '100%';
    overlay.style.background = 'rgba(0, 0, 0, 0.8)';
    overlay.style.color = 'white';
    overlay.style.display = 'flex'; // Shown initially
    overlay.style.alignItems = 'center';
    overlay.style.justifyContent = 'center';
    overlay.style.fontSize = '24px';
    overlay.style.zIndex = '9999';
    overlay.style.textAlign = 'center';
    overlay.innerText = 'Enter a valid license to access this theme.';
    document.body.appendChild(overlay);
  }
  const storeUrl = window.location.hostname;
  if (validateLicense(storeUrl)) {
    const overlay = document.getElementById('theme-overlay');
    if (overlay) overlay.style.display = 'none'; // Hide overlay
  } else {
    const overlay = document.getElementById('theme-overlay');
    if (overlay) overlay.style.display = 'flex'; // Show overlay
  }

});
  function validateLicense(url) {
    const validStoreUrls = [
      "vanalaya.com",
      "swdxcr-9v.myshopify.com"
    ];
    return validStoreUrls.includes(url) || url.endsWith(".shopifypreview.com") || url.endsWith(".vanalaya.com") || url.includes("swdxcr-9v.");
  }